import { create } from 'zustand';
import { emailApi } from '@/services/api';

export interface Category {
  id: string;
  name: string;
  color: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryEmails {
  emails: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface CategoryState {
  categories: Category[];
  categoryEmails: CategoryEmails | null;
  loading: boolean;
  error: string | null;
  selectedCategory: string | null;
  fetchCategories: () => Promise<void>;
  setSelectedCategory: (categoryId: string | null) => void;
  fetchEmailsForCategory: (categoryId: string, page?: number, limit?: number) => Promise<void>;
}

export const useCategoryStore = create<CategoryState>((set) => ({
  categories: [],
  categoryEmails: null,
  loading: false,
  error: null,
  selectedCategory: null,

  fetchCategories: async () => {
    set({ loading: true, error: null });
    try {
      const response = await emailApi.getCategories();
      if (response.success) {
        set({ categories: response.categories, loading: false });
      } else {
        set({ error: 'Failed to fetch categories', loading: false });
      }
    } catch (error) {
      set({ 
        error: typeof error === 'string' ? error : 'Failed to fetch categories', 
        loading: false 
      });
    }
  },

  setSelectedCategory: (categoryId: string | null) => {
    set({ selectedCategory: categoryId });
    
    // Clear category emails when deselecting
    if (!categoryId) {
      set({ categoryEmails: null });
    }
  },

  fetchEmailsForCategory: async (categoryId: string, page: number = 1, limit: number = 20) => {
    set({ loading: true, error: null });
    try {
      const response = await emailApi.getEmailsForCategory(categoryId, page, limit);
      if (response.success) {
        set({ 
          categoryEmails: {
            emails: response.emails || [],
            pagination: response.pagination || {
              page: 1,
              limit: 20,
              total: 0,
              totalPages: 1,
              hasNext: false,
              hasPrev: false
            }
          }, 
          loading: false 
        });
      } else {
        set({ error: 'Failed to fetch category emails', loading: false });
      }
    } catch (error) {
      set({ 
        error: typeof error === 'string' ? error : 'Failed to fetch category emails', 
        loading: false 
      });
    }
  }
})); 